// Dark Requiem — Procedural Audio + Music + Speech
// - Ambient pads (gentle sine-based, not harsh drones)
// - Slow melodic layer using minor-pentatonic arpeggios
// - Web Speech API narration (clean voice for scene text, whispered for hallucinations)
// - Master volume control persisted via localStorage

const LS_VOLUME = "dr_volume";
const LS_MUTED = "dr_muted";
const LS_VOICE_ON = "dr_voice_on";
const LS_MUSIC_ON = "dr_music_on";

// Minor pentatonic scale (A minor: A C D E G) in Hz across three octaves
const SCALE = [
  110.00, 130.81, 146.83, 164.81, 196.00,   // A2 C3 D3 E3 G3
  220.00, 261.63, 293.66, 329.63, 392.00,   // A3 C4 D4 E4 G4
  440.00, 523.25, 587.33, 659.25, 783.99,   // A4 C5 D5 E5 G5
];

class AudioEngine {
  constructor() {
    this.ctx = null;
    this.master = null;
    this.reverbBus = null;
    this.ambient = null;
    this.musicTimer = null;
    this.heartbeatTimer = null;
    this.enabled = false;
    this.muted = localStorage.getItem(LS_MUTED) === "1";
    this.volume = parseFloat(localStorage.getItem(LS_VOLUME) ?? "0.55");
    this.voiceOn = localStorage.getItem(LS_VOICE_ON) !== "0";
    this.musicOn = localStorage.getItem(LS_MUSIC_ON) !== "0";
    this.currentMode = null;
    this.voices = [];
    this.preferredVoiceName = localStorage.getItem("dr_voice_name") || null;
    this._loadVoices();
  }

  _loadVoices() {
    if (!("speechSynthesis" in window)) return;
    const load = () => {
      this.voices = window.speechSynthesis.getVoices();
      // Persist the preferred voice name if user hasn't chosen one
      const saved = localStorage.getItem("dr_voice_name");
      this.preferredVoiceName = saved || null;
    };
    load();
    window.speechSynthesis.onvoiceschanged = load;
  }

  setPreferredVoice(name) {
    this.preferredVoiceName = name || null;
    if (name) localStorage.setItem("dr_voice_name", name);
    else localStorage.removeItem("dr_voice_name");
  }

  getVoices() { return this.voices || []; }

  _pickVoice(kind = "narrator") {
    if (!this.voices.length) return null;

    // If user explicitly picked a voice, always use it
    if (this.preferredVoiceName) {
      const chosen = this.voices.find((v) => v.name === this.preferredVoiceName);
      if (chosen) return chosen;
    }

    const en = this.voices.filter((v) => /en(-|_|$)/i.test(v.lang));
    const pool = en.length ? en : this.voices;

    if (kind === "whisper") {
      // Whisper: soft female voice, if available
      return pool.find((v) => /female|zira|samantha|karen|jenny|libby|sonia|kate|serena/i.test(v.name))
          || pool.find((v) => /google|natural|premium|neural/i.test(v.name))
          || pool[0];
    }

    // Narrator — "Ancient Vampire Lord" hunt list: prefer darkest, deepest male voices available
    const vampireHuntList = [
      /daniel/i,           // macOS — deep British male
      /alex/i,             // macOS — classic deep US male
      /fred/i,             // macOS — very deep, low voice
      /david/i,            // Windows — Microsoft David Desktop, deep US male
      /george/i,           // Microsoft — deep British male
      /guy/i,              // Microsoft neural — deep US
      /ryan/i,             // Microsoft — deep British
      /brian/i,            // deep male
      /james/i,            // deep AU male
      /uk english male/i,  // Chrome — deep British
      /google uk english male/i,
      /male/i,             // any male voice
      /natural|premium|neural/i,
    ];
    for (const re of vampireHuntList) {
      const v = pool.find((v) => re.test(v.name));
      if (v) return v;
    }
    return pool[0];
  }

  _ensure() {
    if (this.ctx) return true;
    try {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      if (!Ctx) return false;
      this.ctx = new Ctx();
      this.master = this.ctx.createGain();
      this.master.gain.value = this.muted ? 0 : this.volume;
      this.reverbBus = this._makeReverb(3.0, 3.5);
      this.reverbBus.output.connect(this.master);
      this.master.connect(this.ctx.destination);
      return true;
    } catch (e) {
      console.warn("Audio ctx failed", e);
      return false;
    }
  }

  // Synchronous unlock — MUST be called inside a user gesture handler.
  // Creates AudioContext + kicks resume + primes speech synthesis in the same tick.
  unlock() {
    if (this.enabled) return true;
    if (!this._ensure()) return false;
    // Kick resume synchronously — don't await, don't block gesture
    if (this.ctx.state === "suspended") {
      this.ctx.resume().catch((e) => console.warn("Audio resume failed", e));
    }
    this.enabled = true;
    this._primeSpeech();
    return true;
  }

  // Fire an inaudible utterance inside the user gesture so subsequent
  // speak() calls from effects are allowed by the browser.
  _primeSpeech() {
    if (!("speechSynthesis" in window)) return;
    try {
      const u = new SpeechSynthesisUtterance(" ");
      u.volume = 0;
      u.rate = 2;
      window.speechSynthesis.cancel();
      window.speechSynthesis.speak(u);
    } catch (e) { console.warn("Speech prime failed", e); }
  }

  setMuted(m) {
    this.muted = m;
    localStorage.setItem(LS_MUTED, m ? "1" : "0");
    if (this.master) this.master.gain.setTargetAtTime(m ? 0 : this.volume, this.ctx.currentTime, 0.05);
    if (m && "speechSynthesis" in window) window.speechSynthesis.cancel();
  }
  setVolume(v) {
    this.volume = Math.max(0, Math.min(1, v));
    localStorage.setItem(LS_VOLUME, String(this.volume));
    if (this.master && !this.muted) this.master.gain.setTargetAtTime(this.volume, this.ctx.currentTime, 0.05);
  }
  setVoiceOn(on) {
    this.voiceOn = on;
    localStorage.setItem(LS_VOICE_ON, on ? "1" : "0");
    if (!on && "speechSynthesis" in window) window.speechSynthesis.cancel();
  }
  setMusicOn(on) {
    this.musicOn = on;
    localStorage.setItem(LS_MUSIC_ON, on ? "1" : "0");
    if (!on) this._stopMusicLayer();
    else if (this.enabled && this.currentMode) this._startMusicLayer(this.currentMode);
  }

  // ---- Reverb ----
  _makeReverb(duration = 3, decay = 3) {
    const rate = this.ctx.sampleRate;
    const length = Math.floor(rate * duration);
    const impulse = this.ctx.createBuffer(2, length, rate);
    for (let ch = 0; ch < 2; ch++) {
      const data = impulse.getChannelData(ch);
      for (let i = 0; i < length; i++) {
        data[i] = (Math.random() * 2 - 1) * Math.pow(1 - i / length, decay);
      }
    }
    const convolver = this.ctx.createConvolver();
    convolver.buffer = impulse;
    const input = this.ctx.createGain();
    const wet = this.ctx.createGain();
    wet.gain.value = 0.45;
    const output = this.ctx.createGain();
    input.connect(convolver);
    convolver.connect(wet);
    wet.connect(output);
    input.connect(output);
    return { input, output };
  }

  // ---- Gentle ambient pad (sine-based, not vibrating) ----
  startAmbient(mode) {
    if (!this.enabled || !this._ensure()) return;
    if (this.currentMode === mode && this.ambient) return;
    this.stopAmbient();
    this.currentMode = mode;
    this._startPadLayer(mode);
    if (this.musicOn) this._startMusicLayer(mode);
  }

  _startPadLayer(mode) {
    // Each mode has its own musical fingerprint:
    // - exploration: hollow sine pad, sparse
    // - stealth: low tension drone with subtle wobble
    // - danger: aggressive detuned pad + dissonant top + faster wobble
    // - grief: warm minor chord pad, slow
    // - safe: bright warm high pad
    // - credits: melancholic wide pad
    const cfg = {
      exploration: { rootMul: 1,    gain: 0.18, brightness: 500, noise: 0.010, wobble: 0.08 },
      stealth:     { rootMul: 0.75, gain: 0.22, brightness: 350, noise: 0.022, wobble: 0.15, tension: true },
      danger:      { rootMul: 1.5,  gain: 0.30, brightness: 900, noise: 0.045, wobble: 0.9, sting: true, dissonance: true },
      grief:       { rootMul: 1.25, gain: 0.20, brightness: 700, noise: 0.008, wobble: 0.05, warm: true, minorThird: true },
      safe:        { rootMul: 2,    gain: 0.16, brightness: 1100, noise: 0.005, wobble: 0.06, warm: true },
      credits:     { rootMul: 1,    gain: 0.20, brightness: 650, noise: 0.010, wobble: 0.07, minorThird: true },
    }[mode] || { rootMul: 1, gain: 0.18, brightness: 500, noise: 0.010, wobble: 0.08 };

    const root = 110 * cfg.rootMul; // A2 root

    const bank = { oscs: [], noise: null, output: this.ctx.createGain(), lfos: [], filters: [] };
    bank.output.gain.value = 0;
    bank.output.connect(this.reverbBus.input);
    bank.output.gain.setTargetAtTime(cfg.gain, this.ctx.currentTime + 0.05, cfg.sting ? 0.6 : 1.8);

    // Chord voicing depends on mood
    // For grief / minorThird — root + minor 3rd + 5th (Am chord shape)
    // For safe/exploration — root + 5th + octave (open fifth)
    // For danger — root + b5 + 7th (tritone + minor 7th) — unstable
    let chord;
    if (cfg.dissonance) chord = [1, Math.pow(2, 6/12), Math.pow(2, 10/12)]; // root + b5 + b7
    else if (cfg.minorThird) chord = [1, Math.pow(2, 3/12), 1.5]; // root + minor 3rd + 5th
    else chord = [1, 1.5, 2]; // open fifth

    for (const mul of chord) {
      const o = this.ctx.createOscillator();
      o.type = "sine";
      o.frequency.value = root * mul;
      o.detune.value = (Math.random() * 6) - 3;
      const g = this.ctx.createGain();
      g.gain.value = mul === 1 ? 0.5 : 0.25;
      const filt = this.ctx.createBiquadFilter();
      filt.type = "lowpass";
      filt.frequency.value = cfg.brightness;
      filt.Q.value = cfg.dissonance ? 4 : 0.7;
      o.connect(filt); filt.connect(g); g.connect(bank.output);
      o.start();
      bank.oscs.push(o);
      bank.filters.push(filt);
    }

    // Filter wobble LFO — subtle in exploration, aggressive in danger
    const lfo = this.ctx.createOscillator();
    lfo.frequency.value = cfg.wobble;
    lfo.type = "sine";
    const lfoGain = this.ctx.createGain();
    lfoGain.gain.value = cfg.brightness * (cfg.dissonance ? 0.6 : 0.25);
    lfo.connect(lfoGain);
    // Modulate the first filter's frequency for a breathing effect
    if (bank.filters[0]) lfoGain.connect(bank.filters[0].frequency);
    lfo.start();
    bank.lfos.push(lfo);

    // Danger sting: dissonant tritone bell above
    if (cfg.sting) {
      const o = this.ctx.createOscillator();
      o.type = "sawtooth";
      o.frequency.value = root * 4.11; // ugly ratio
      const g = this.ctx.createGain();
      g.gain.value = 0.05;
      const bandFilt = this.ctx.createBiquadFilter();
      bandFilt.type = "bandpass";
      bandFilt.frequency.value = 1400;
      bandFilt.Q.value = 8;
      o.connect(bandFilt); bandFilt.connect(g); g.connect(bank.output);
      o.start();
      bank.oscs.push(o);
    }

    // Filtered noise floor — heavier for danger for "static/breathing" texture
    if (cfg.noise) {
      const noise = this._createNoiseSource();
      const nFilt = this.ctx.createBiquadFilter();
      nFilt.type = "lowpass";
      nFilt.frequency.value = cfg.dissonance ? 1400 : 800;
      const nGain = this.ctx.createGain();
      nGain.gain.value = cfg.noise;
      noise.connect(nFilt); nFilt.connect(nGain); nGain.connect(bank.output);
      noise.start();
      bank.noise = noise;
    }

    // Occasional metallic clank/creak in stealth and danger
    if (cfg.tension || cfg.dissonance) {
      bank.clankTimer = setInterval(() => {
        if (Math.random() < (cfg.dissonance ? 0.6 : 0.3)) this._clank();
      }, (cfg.dissonance ? 2200 : 4200) + Math.random() * 2500);
    }

    this.ambient = bank;
  }

  // Buildup sting — call when entering a scene with heightened tension
  buildup() {
    if (!this.enabled) return;
    const t = this.ctx.currentTime;
    // Rising minor-3rd swell over ~1.5s
    for (const [mul, delay] of [[1, 0], [Math.pow(2, 3/12), 0.35], [Math.pow(2, 7/12), 0.7]]) {
      const o = this.ctx.createOscillator();
      o.type = "sine";
      o.frequency.setValueAtTime(55 * mul, t + delay);
      o.frequency.exponentialRampToValueAtTime(110 * mul, t + delay + 1.5);
      const g = this.ctx.createGain();
      g.gain.setValueAtTime(0, t + delay);
      g.gain.linearRampToValueAtTime(0.20, t + delay + 0.8);
      g.gain.exponentialRampToValueAtTime(0.001, t + delay + 2.0);
      const f = this.ctx.createBiquadFilter();
      f.type = "lowpass"; f.frequency.value = 500; f.Q.value = 3;
      o.connect(f); f.connect(g); g.connect(this.reverbBus.input);
      o.start(t + delay); o.stop(t + delay + 2.1);
    }
    // Riser noise
    const noise = this._createNoiseSource();
    const nFilt = this.ctx.createBiquadFilter();
    nFilt.type = "bandpass"; nFilt.Q.value = 5;
    nFilt.frequency.setValueAtTime(200, t);
    nFilt.frequency.exponentialRampToValueAtTime(4000, t + 1.6);
    const nGain = this.ctx.createGain();
    nGain.gain.setValueAtTime(0, t);
    nGain.gain.linearRampToValueAtTime(0.14, t + 1.4);
    nGain.gain.exponentialRampToValueAtTime(0.001, t + 1.8);
    noise.connect(nFilt); nFilt.connect(nGain); nGain.connect(this.master);
    noise.start(t); noise.stop(t + 1.85);
  }

  // Called when a Metallic clank should sound naturally
  _clank() {
    if (!this.enabled) return;
    const t = this.ctx.currentTime;
    const o = this.ctx.createOscillator();
    o.type = "square";
    o.frequency.setValueAtTime(280 + Math.random() * 260, t);
    o.frequency.exponentialRampToValueAtTime(90, t + 0.28);
    const g = this.ctx.createGain();
    g.gain.setValueAtTime(0, t);
    g.gain.linearRampToValueAtTime(0.08, t + 0.005);
    g.gain.exponentialRampToValueAtTime(0.001, t + 0.32);
    const f = this.ctx.createBiquadFilter();
    f.type = "bandpass";
    f.frequency.value = 800;
    f.Q.value = 6;
    o.connect(f); f.connect(g); g.connect(this.reverbBus.input);
    o.start(t); o.stop(t + 0.35);
  }

  // ---- Music layer: mode-driven melodic patterns ----
  _startMusicLayer(mode) {
    this._stopMusicLayer();
    if (!this.musicOn) return;

    // Rhythm and note pattern varies dramatically per mode
    // - exploration: slow, sparse pentatonic
    // - stealth: very slow, low register
    // - danger: fast, dissonant chromatic runs + occasional sub hit
    // - grief: slow minor-key motif (root -> minor 3rd -> 5th)
    // - safe: gentle high pentatonic
    // - credits: slow melancholic minor sequence
    const cfg = {
      exploration: { interval: 3200, octaves: [1, 2], gain: 0.22, waveform: "sine", pattern: "penta" },
      stealth:     { interval: 4500, octaves: [0, 1], gain: 0.18, waveform: "sine", pattern: "penta" },
      danger:      { interval: 900,  octaves: [0, 1, 2], gain: 0.28, waveform: "triangle", pattern: "chromatic", subHit: true },
      grief:       { interval: 3200, octaves: [1, 2], gain: 0.26, waveform: "sine", pattern: "minor" },
      safe:        { interval: 4200, octaves: [2, 2], gain: 0.24, waveform: "sine", pattern: "penta" },
      credits:     { interval: 3800, octaves: [1, 2], gain: 0.24, waveform: "sine", pattern: "minor" },
    }[mode] || { interval: 3400, octaves: [1, 2], gain: 0.22, waveform: "sine", pattern: "penta" };

    // Note pools per pattern
    // SCALE indices are: 0-4=octave1(A2..G3), 5-9=octave2(A3..G4), 10-14=octave3(A4..G5)
    const pentaOffsets = [0, 1, 2, 3, 4]; // A C D E G
    const minorOffsets = [0, 1, 3];       // A C E  (i.e. Am chord tones)
    // Chromatic dissonant pool: A, Bb, C, C#, D#, F, F# (mostly tritones/leading tones)
    // Realised as pentatonic + intentional half-steps for chaos
    const chromaticOffsets = [0, 1, 2, 4]; // penta minus 1 note, but with rapid repeat = "buzzing"

    const noteBank = cfg.pattern === "minor" ? minorOffsets
                   : cfg.pattern === "chromatic" ? chromaticOffsets
                   : pentaOffsets;

    let stepIdx = 0;

    const playNote = () => {
      if (!this.enabled || !this.musicOn) return;
      const t = this.ctx.currentTime;
      const oct = cfg.octaves[stepIdx % cfg.octaves.length];
      const noteOff = noteBank[stepIdx % noteBank.length];
      // For danger, occasionally jump octave randomly
      const octJitter = cfg.pattern === "chromatic" && Math.random() < 0.4 ? -1 : 0;
      const finalOct = Math.max(0, Math.min(2, oct + octJitter));
      const noteIdx = finalOct * 5 + noteOff;
      const freq = SCALE[noteIdx];
      stepIdx++;

      const o = this.ctx.createOscillator();
      o.type = cfg.waveform;
      o.frequency.value = freq;
      // Slight detune for danger = tension
      if (cfg.pattern === "chromatic") o.detune.value = (Math.random() * 20) - 10;
      const g = this.ctx.createGain();
      const decay = cfg.pattern === "chromatic" ? 1.4 : 3.5;
      g.gain.setValueAtTime(0, t);
      g.gain.linearRampToValueAtTime(cfg.gain, t + 0.04);
      g.gain.exponentialRampToValueAtTime(0.001, t + decay);
      const filt = this.ctx.createBiquadFilter();
      filt.type = "lowpass";
      filt.frequency.value = cfg.pattern === "chromatic" ? 2000 : 3000;
      o.connect(filt); filt.connect(g); g.connect(this.reverbBus.input);
      o.start(t); o.stop(t + decay + 0.1);

      // Optional harmonic
      if (cfg.pattern !== "chromatic" && Math.random() < 0.4) {
        const h = this.ctx.createOscillator();
        h.type = "sine";
        h.frequency.value = freq * (cfg.pattern === "minor" ? Math.pow(2, 3/12) : 1.5);
        const hg = this.ctx.createGain();
        hg.gain.setValueAtTime(0, t);
        hg.gain.linearRampToValueAtTime(cfg.gain * 0.45, t + 0.06);
        hg.gain.exponentialRampToValueAtTime(0.001, t + 3);
        h.connect(hg); hg.connect(this.reverbBus.input);
        h.start(t); h.stop(t + 3.1);
      }

      // Sub hit for danger — every ~4th note, a heavy kick
      if (cfg.subHit && stepIdx % 4 === 0) {
        const s = this.ctx.createOscillator();
        s.type = "sine";
        s.frequency.setValueAtTime(70, t);
        s.frequency.exponentialRampToValueAtTime(28, t + 0.35);
        const sg = this.ctx.createGain();
        sg.gain.setValueAtTime(0, t);
        sg.gain.linearRampToValueAtTime(0.35, t + 0.02);
        sg.gain.exponentialRampToValueAtTime(0.001, t + 0.5);
        s.connect(sg); sg.connect(this.master);
        s.start(t); s.stop(t + 0.55);
      }
    };

    playNote();
    this.musicTimer = setInterval(playNote, cfg.interval + (Math.random() * 400 - 200));
  }
  _stopMusicLayer() {
    if (this.musicTimer) { clearInterval(this.musicTimer); this.musicTimer = null; }
  }

  stopAmbient() {
    this._stopMusicLayer();
    if (!this.ambient) return;
    const b = this.ambient;
    const now = this.ctx.currentTime;
    b.output.gain.cancelScheduledValues(now);
    b.output.gain.setTargetAtTime(0, now, 0.4);
    if (b.clankTimer) { clearInterval(b.clankTimer); b.clankTimer = null; }
    setTimeout(() => {
      try {
        b.oscs.forEach((o) => o.stop());
        if (b.lfos) b.lfos.forEach((l) => l.stop());
        if (b.noise) b.noise.stop();
        b.output.disconnect();
      } catch (e) { console.warn("Ambient teardown", e); }
    }, 1200);
    this.ambient = null;
    this.currentMode = null;
  }

  _createNoiseSource() {
    const bufferSize = this.ctx.sampleRate * 2;
    const buffer = this.ctx.createBuffer(1, bufferSize, this.ctx.sampleRate);
    const data = buffer.getChannelData(0);
    let b0 = 0, b1 = 0, b2 = 0;
    for (let i = 0; i < bufferSize; i++) {
      const white = Math.random() * 2 - 1;
      b0 = 0.99765 * b0 + white * 0.099046;
      b1 = 0.96300 * b1 + white * 0.29327;
      b2 = 0.57000 * b2 + white * 1.0405;
      data[i] = (b0 + b1 + b2 + white * 0.1848) * 0.11;
    }
    const source = this.ctx.createBufferSource();
    source.buffer = buffer;
    source.loop = true;
    return source;
  }

  jumpscare() {
    this.unlock();
    if (!this.enabled) return;
    const t = this.ctx.currentTime;
    const noise = this._createNoiseSource();
    const nFilt = this.ctx.createBiquadFilter();
    nFilt.type = "highpass"; nFilt.frequency.value = 800;
    const nGain = this.ctx.createGain();
    nGain.gain.setValueAtTime(0, t);
    nGain.gain.linearRampToValueAtTime(0.45, t + 0.02);
    nGain.gain.exponentialRampToValueAtTime(0.001, t + 0.55);
    noise.connect(nFilt); nFilt.connect(nGain); nGain.connect(this.master);
    noise.start(t); noise.stop(t + 0.6);

    const o = this.ctx.createOscillator();
    o.type = "sawtooth";
    o.frequency.setValueAtTime(220, t);
    o.frequency.exponentialRampToValueAtTime(35, t + 0.9);
    const g = this.ctx.createGain();
    g.gain.setValueAtTime(0, t);
    g.gain.linearRampToValueAtTime(0.5, t + 0.03);
    g.gain.exponentialRampToValueAtTime(0.001, t + 1.1);
    const f = this.ctx.createBiquadFilter();
    f.type = "lowpass"; f.frequency.value = 900; f.Q.value = 3;
    o.connect(f); f.connect(g); g.connect(this.master);
    o.start(t); o.stop(t + 1.2);

    const s = this.ctx.createOscillator();
    s.type = "sawtooth";
    s.frequency.setValueAtTime(2400, t);
    s.frequency.exponentialRampToValueAtTime(900, t + 0.5);
    const sg = this.ctx.createGain();
    sg.gain.setValueAtTime(0, t);
    sg.gain.linearRampToValueAtTime(0.12, t + 0.01);
    sg.gain.exponentialRampToValueAtTime(0.001, t + 0.5);
    const sf = this.ctx.createBiquadFilter();
    sf.type = "bandpass"; sf.frequency.value = 1800; sf.Q.value = 12;
    s.connect(sf); sf.connect(sg); sg.connect(this.reverbBus.input);
    s.start(t); s.stop(t + 0.6);
  }

  click() {
    this.unlock();
    if (!this.enabled) return;
    const t = this.ctx.currentTime;
    const o = this.ctx.createOscillator();
    o.type = "square";
    o.frequency.setValueAtTime(2200 + Math.random() * 400, t);
    const g = this.ctx.createGain();
    g.gain.setValueAtTime(0, t);
    g.gain.linearRampToValueAtTime(0.05, t + 0.002);
    g.gain.exponentialRampToValueAtTime(0.001, t + 0.05);
    const f = this.ctx.createBiquadFilter();
    f.type = "highpass"; f.frequency.value = 1500;
    o.connect(f); f.connect(g); g.connect(this.master);
    o.start(t); o.stop(t + 0.06);
  }

  heartbeat(enable) {
    if (this.heartbeatTimer) { clearInterval(this.heartbeatTimer); this.heartbeatTimer = null; }
    if (!enable || !this.enabled) return;
    const beat = () => {
      const t = this.ctx.currentTime;
      for (const [offset, gain] of [[0, 0.28], [0.14, 0.20]]) {
        const o = this.ctx.createOscillator();
        o.type = "sine";
        o.frequency.setValueAtTime(90, t + offset);
        o.frequency.exponentialRampToValueAtTime(38, t + offset + 0.18);
        const g = this.ctx.createGain();
        g.gain.setValueAtTime(0, t + offset);
        g.gain.linearRampToValueAtTime(gain, t + offset + 0.02);
        g.gain.exponentialRampToValueAtTime(0.001, t + offset + 0.28);
        o.connect(g); g.connect(this.master);
        o.start(t + offset); o.stop(t + offset + 0.3);
      }
    };
    beat();
    this.heartbeatTimer = setInterval(beat, 1400);
  }

  // ---- Voice narration & whisper via Web Speech API ----
  speak(text, opts = {}) {
    if (!this.voiceOn || !text || !("speechSynthesis" in window)) return;
    const clean = String(text).replace(/\s+/g, " ").trim();
    if (!clean) return;
    // Chrome bug workaround: cancel any queued or 15s-hung utterance first
    try { window.speechSynthesis.cancel(); } catch (e) { console.warn("Speech cancel", e); }
    const u = new SpeechSynthesisUtterance(clean);
    const isWhisper = !!opts.whisper;
    u.rate = isWhisper ? 0.72 : 0.94;
    u.pitch = isWhisper ? 0.55 : 1.0;
    u.volume = this.muted ? 0 : (isWhisper ? Math.min(1, this.volume + 0.15) : Math.min(1, this.volume + 0.25));
    const v = this._pickVoice(isWhisper ? "whisper" : "narrator");
    if (v) { u.voice = v; u.lang = v.lang; } else { u.lang = "en-US"; }
    // If voices haven't loaded yet, retry once when they do
    if (!v && this.voices.length === 0 && "speechSynthesis" in window) {
      const retry = () => {
        window.speechSynthesis.removeEventListener("voiceschanged", retry);
        this.voices = window.speechSynthesis.getVoices();
        const rv = this._pickVoice(isWhisper ? "whisper" : "narrator");
        if (rv) { u.voice = rv; u.lang = rv.lang; }
        try { window.speechSynthesis.speak(u); } catch (e) { console.warn("Speech retry failed", e); }
      };
      window.speechSynthesis.addEventListener("voiceschanged", retry, { once: true });
      // Also try to speak now — many browsers still play with a default voice
    }
    try { window.speechSynthesis.speak(u); } catch (e) { console.warn("Speech failed", e); }
  }

  cancelSpeech() {
    if ("speechSynthesis" in window) {
      try { window.speechSynthesis.cancel(); } catch (e) { console.warn("Speech cancel", e); }
    }
  }

  // Short procedural whisper-hiss cue — plays while the LLM whisper streams in
  whisper() {
    if (!this.enabled) return;
    const t = this.ctx.currentTime;
    for (let i = 0; i < 3; i++) {
      const noise = this._createNoiseSource();
      const filt = this.ctx.createBiquadFilter();
      filt.type = "bandpass";
      filt.frequency.value = 1200 + i * 900;
      filt.Q.value = 20;
      const g = this.ctx.createGain();
      const startT = t + i * 0.12;
      g.gain.setValueAtTime(0, startT);
      g.gain.linearRampToValueAtTime(0.06 - i * 0.015, startT + 0.9);
      g.gain.exponentialRampToValueAtTime(0.001, startT + 1.1);
      noise.connect(filt); filt.connect(g); g.connect(this.reverbBus.input);
      noise.start(startT); noise.stop(startT + 1.2);
    }
  }
}

export const audio = new AudioEngine();
